/**
 * LeadsCRMTab.js - Customer Lead Management & Follow-Up CRM
 * Track website leads, contact forms, referral sources, manage statuses, and view sales pipelines
 */
import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Users, UserPlus, Phone, Mail, MessageSquare, Calendar, 
  ChevronDown, ChevronRight, Search, Filter, Plus, Edit, Trash2,
  CheckCircle, Clock, AlertCircle, TrendingUp, DollarSign,
  PhoneCall, MailOpen, ArrowRight, Star, Building, MapPin,
  RefreshCw, Eye, Send, FileText, Target, Zap
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import api from '../../services/api';

// Lead Status Colors
const statusColors = {
  new: { bg: 'bg-blue-500/20', text: 'text-blue-400', border: 'border-blue-500/30' },
  contact_attempted: { bg: 'bg-amber-500/20', text: 'text-amber-400', border: 'border-amber-500/30' },
  contacted: { bg: 'bg-cyan-500/20', text: 'text-cyan-400', border: 'border-cyan-500/30' },
  interested: { bg: 'bg-green-500/20', text: 'text-green-400', border: 'border-green-500/30' },
  quote_requested: { bg: 'bg-purple-500/20', text: 'text-purple-400', border: 'border-purple-500/30' },
  quote_sent: { bg: 'bg-indigo-500/20', text: 'text-indigo-400', border: 'border-indigo-500/30' },
  follow_up_due: { bg: 'bg-orange-500/20', text: 'text-orange-400', border: 'border-orange-500/30' },
  booking_created: { bg: 'bg-emerald-500/20', text: 'text-emerald-400', border: 'border-emerald-500/30' },
  converted: { bg: 'bg-[var(--primary)]/20', text: 'text-[var(--primary)]', border: 'border-[var(--primary)]/30' },
  not_interested: { bg: 'bg-gray-500/20', text: 'text-gray-400', border: 'border-gray-500/30' },
  unreachable: { bg: 'bg-red-500/20', text: 'text-red-400', border: 'border-red-500/30' },
  do_not_contact: { bg: 'bg-red-800/20', text: 'text-red-300', border: 'border-red-800/30' }
};

const statusLabels = {
  new: 'New',
  contact_attempted: 'Contact Attempted',
  contacted: 'Contacted',
  interested: 'Interested',
  quote_requested: 'Quote Requested',
  quote_sent: 'Quote Sent',
  follow_up_due: 'Follow-Up Due',
  booking_created: 'Booking Created',
  converted: 'Converted',
  not_interested: 'Not Interested',
  unreachable: 'Unreachable',
  do_not_contact: 'Do Not Contact'
};

const sourceLabels = {
  website_contact: 'Website Contact',
  phone_inquiry: 'Phone Inquiry',
  referral: 'Referral',
  business_outreach: 'Business Outreach',
  marketing_campaign: 'Marketing Campaign',
  incomplete_booking: 'Incomplete Booking',
  walk_in: 'Walk-In',
  social_media: 'Social Media',
  other: 'Other'
};

const LeadsCRMTab = () => {
  const [loading, setLoading] = useState(true);
  const [leads, setLeads] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [followupsDue, setFollowupsDue] = useState({ overdue: [], due_today: [] });
  
  // Filters
  const [statusFilter, setStatusFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Modals
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  
  // Form state
  const [newLead, setNewLead] = useState({
    name: '',
    email: '',
    phone: '',
    business_name: '',
    preferred_contact: 'phone',
    service_interest: [],
    service_location: '',
    source: 'other',
    notes: '',
    consent_given: true
  });
  
  const [contactLog, setContactLog] = useState({
    contact_method: 'phone',
    outcome: 'connected',
    notes: '',
    next_followup_date: '',
    quote_provided: ''
  });

  // Fetch leads
  const fetchLeads = useCallback(async () => {
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (sourceFilter !== 'all') params.source = sourceFilter;
      
      const response = await api.get('/leads', { params });
      setLeads(response.data || []);
    } catch (error) {
      console.error('Failed to fetch leads:', error);
    }
  }, [statusFilter, sourceFilter]);

  // Fetch analytics
  const fetchAnalytics = useCallback(async () => {
    try {
      const response = await api.get('/leads/dashboard/analytics');
      setAnalytics(response.data);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
    }
  }, []);

  // Fetch follow-ups due
  const fetchFollowups = useCallback(async () => {
    try {
      const response = await api.get('/leads/dashboard/followups-due');
      setFollowupsDue(response.data);
    } catch (error) {
      console.error('Failed to fetch followups:', error);
    }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchLeads(), fetchAnalytics(), fetchFollowups()]);
      setLoading(false);
    };
    loadData();
  }, [fetchLeads, fetchAnalytics, fetchFollowups]);

  // Create new lead
  const handleCreateLead = async (e) => {
    e.preventDefault();
    if (!newLead.name) {
      toast.error('Please enter a name');
      return;
    }
    
    try {
      const response = await api.post('/leads', newLead);
      if (response.data.status === 'duplicate') {
        toast.warning('A lead with this contact info already exists');
      } else {
        toast.success('Lead created successfully');
        setShowAddModal(false);
        setNewLead({
          name: '',
          email: '',
          phone: '',
          business_name: '',
          preferred_contact: 'phone',
          service_interest: [],
          service_location: '',
          source: 'other',
          notes: '',
          consent_given: true
        });
        fetchLeads();
        fetchAnalytics();
      }
    } catch (error) {
      toast.error('Failed to create lead');
    }
  };

  // Update lead status
  const handleUpdateStatus = async (leadId, newStatus) => {
    try {
      await api.put(`/leads/${leadId}`, { status: newStatus });
      toast.success(`Status updated to ${statusLabels[newStatus]}`);
      fetchLeads();
      fetchAnalytics();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  // Log contact
  const handleLogContact = async (e) => {
    e.preventDefault();
    if (!selectedLead) return;
    
    try {
      await api.post(`/leads/${selectedLead.id}/contact`, {
        ...contactLog,
        quote_provided: contactLog.quote_provided ? parseFloat(contactLog.quote_provided) : null
      });
      toast.success('Contact logged successfully');
      setShowContactModal(false);
      setContactLog({
        contact_method: 'phone',
        outcome: 'connected',
        notes: '',
        next_followup_date: '',
        quote_provided: ''
      });
      fetchLeads();
      fetchFollowups();
    } catch (error) {
      toast.error('Failed to log contact');
    }
  };

  // Filter leads
  const filteredLeads = leads.filter(lead => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        lead.name?.toLowerCase().includes(query) ||
        lead.email?.toLowerCase().includes(query) ||
        lead.phone?.includes(query) ||
        lead.business_name?.toLowerCase().includes(query)
      );
    }
    return true;
  });

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <RefreshCw className="w-8 h-8 animate-spin text-[var(--primary)]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-[var(--text-primary)]">Customer Leads & CRM</h2>
          <p className="text-sm text-[var(--text-muted)]">Track and manage your sales pipeline</p>
        </div>
        <Button onClick={() => setShowAddModal(true)} className="btn-primary" data-testid="add-lead-btn">
          <Plus className="w-4 h-4 mr-2" />
          Add New Lead
        </Button>
      </div>

      {/* Analytics Cards */}
      {analytics && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="p-4 rounded-xl bg-[var(--card)] border border-[var(--border)]">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-500/20">
                <Users className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[var(--text-primary)]">{analytics.total_leads}</p>
                <p className="text-xs text-[var(--text-muted)]">Total Leads</p>
              </div>
            </div>
          </div>
          
          <div className="p-4 rounded-xl bg-[var(--card)] border border-[var(--border)]">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-500/20">
                <CheckCircle className="w-5 h-5 text-green-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-green-400">{analytics.converted}</p>
                <p className="text-xs text-[var(--text-muted)]">Converted</p>
              </div>
            </div>
          </div>
          
          <div className="p-4 rounded-xl bg-[var(--card)] border border-[var(--border)]">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-[var(--primary)]/20">
                <TrendingUp className="w-5 h-5 text-[var(--primary)]" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[var(--primary)]">{analytics.conversion_rate}%</p>
                <p className="text-xs text-[var(--text-muted)]">Conversion Rate</p>
              </div>
            </div>
          </div>
          
          <div className="p-4 rounded-xl bg-[var(--card)] border border-[var(--border)]">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-orange-500/20">
                <Clock className="w-5 h-5 text-orange-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-orange-400">
                  {followupsDue.total_overdue + followupsDue.total_due_today}
                </p>
                <p className="text-xs text-[var(--text-muted)]">Follow-ups Due</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Follow-ups Alert */}
      {(followupsDue.overdue?.length > 0 || followupsDue.due_today?.length > 0) && (
        <div className="p-4 rounded-xl bg-orange-500/10 border border-orange-500/30">
          <div className="flex items-center gap-3 mb-3">
            <AlertCircle className="w-5 h-5 text-orange-400" />
            <h3 className="font-medium text-orange-400">Follow-ups Requiring Attention</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {followupsDue.overdue?.length > 0 && (
              <div>
                <p className="text-xs text-red-400 font-medium mb-2">OVERDUE ({followupsDue.overdue.length})</p>
                <div className="space-y-2">
                  {followupsDue.overdue.slice(0, 3).map(lead => (
                    <div 
                      key={lead.id}
                      className="flex items-center justify-between p-2 rounded-lg bg-red-500/10 cursor-pointer hover:bg-red-500/20 transition-colors"
                      onClick={() => { setSelectedLead(lead); setShowDetailModal(true); }}
                    >
                      <span className="text-sm text-[var(--text-primary)]">{lead.name}</span>
                      <span className="text-xs text-red-400">{formatDate(lead.next_followup_date)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {followupsDue.due_today?.length > 0 && (
              <div>
                <p className="text-xs text-amber-400 font-medium mb-2">DUE TODAY ({followupsDue.due_today.length})</p>
                <div className="space-y-2">
                  {followupsDue.due_today.slice(0, 3).map(lead => (
                    <div 
                      key={lead.id}
                      className="flex items-center justify-between p-2 rounded-lg bg-amber-500/10 cursor-pointer hover:bg-amber-500/20 transition-colors"
                      onClick={() => { setSelectedLead(lead); setShowDetailModal(true); }}
                    >
                      <span className="text-sm text-[var(--text-primary)]">{lead.name}</span>
                      <span className="text-xs text-amber-400">Today</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--text-muted)]" />
          <Input
            placeholder="Search leads..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-[var(--background)]"
            data-testid="search-leads-input"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[180px] bg-[var(--background)]">
            <SelectValue placeholder="All Statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            {Object.entries(statusLabels).map(([value, label]) => (
              <SelectItem key={value} value={value}>{label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={sourceFilter} onValueChange={setSourceFilter}>
          <SelectTrigger className="w-[180px] bg-[var(--background)]">
            <SelectValue placeholder="All Sources" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Sources</SelectItem>
            {Object.entries(sourceLabels).map(([value, label]) => (
              <SelectItem key={value} value={value}>{label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Leads Table */}
      <div className="overflow-x-auto rounded-xl border border-[var(--border)]">
        <table className="w-full">
          <thead className="bg-[var(--card)]">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-[var(--text-muted)] uppercase">Lead</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-[var(--text-muted)] uppercase">Contact</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-[var(--text-muted)] uppercase">Source</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-[var(--text-muted)] uppercase">Status</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-[var(--text-muted)] uppercase">Last Contact</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-[var(--text-muted)] uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[var(--border)]">
            {filteredLeads.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-12 text-center text-[var(--text-muted)]">
                  <Users className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>No leads found</p>
                  <Button onClick={() => setShowAddModal(true)} variant="outline" className="mt-4">
                    <Plus className="w-4 h-4 mr-2" /> Add First Lead
                  </Button>
                </td>
              </tr>
            ) : (
              filteredLeads.map((lead) => (
                <tr key={lead.id} className="hover:bg-[var(--card)] transition-colors">
                  <td className="px-4 py-3">
                    <div>
                      <p className="font-medium text-[var(--text-primary)]">{lead.name}</p>
                      {lead.business_name && (
                        <p className="text-xs text-[var(--text-muted)] flex items-center gap-1">
                          <Building className="w-3 h-3" /> {lead.business_name}
                        </p>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="space-y-1">
                      {lead.phone && (
                        <p className="text-sm text-[var(--text-secondary)] flex items-center gap-1">
                          <Phone className="w-3 h-3" /> {lead.phone}
                        </p>
                      )}
                      {lead.email && (
                        <p className="text-sm text-[var(--text-secondary)] flex items-center gap-1">
                          <Mail className="w-3 h-3" /> {lead.email}
                        </p>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-[var(--text-secondary)]">
                      {sourceLabels[lead.source] || lead.source}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${statusColors[lead.status]?.bg} ${statusColors[lead.status]?.text} ${statusColors[lead.status]?.border} border`}>
                      {statusLabels[lead.status] || lead.status}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-[var(--text-muted)]">
                      {lead.last_contact_date ? formatDate(lead.last_contact_date) : 'Never'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => { setSelectedLead(lead); setShowDetailModal(true); }}
                        className="h-8 w-8 p-0"
                        data-testid={`view-lead-${lead.id}`}
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => { setSelectedLead(lead); setShowContactModal(true); }}
                        className="h-8 w-8 p-0"
                        data-testid={`contact-lead-${lead.id}`}
                      >
                        <PhoneCall className="w-4 h-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Add Lead Modal */}
      <AnimatePresence>
        {showAddModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={() => setShowAddModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-[var(--card)] rounded-2xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-lg font-bold text-[var(--text-primary)] mb-4 flex items-center gap-2">
                <UserPlus className="w-5 h-5 text-[var(--primary)]" />
                Add New Lead
              </h3>
              
              <form onSubmit={handleCreateLead} className="space-y-4">
                <div>
                  <Label>Name *</Label>
                  <Input
                    value={newLead.name}
                    onChange={(e) => setNewLead(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Full name"
                    className="bg-[var(--background)]"
                    data-testid="new-lead-name"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Phone</Label>
                    <Input
                      value={newLead.phone}
                      onChange={(e) => setNewLead(prev => ({ ...prev, phone: e.target.value }))}
                      placeholder="(555) 123-4567"
                      className="bg-[var(--background)]"
                      data-testid="new-lead-phone"
                    />
                  </div>
                  <div>
                    <Label>Email</Label>
                    <Input
                      type="email"
                      value={newLead.email}
                      onChange={(e) => setNewLead(prev => ({ ...prev, email: e.target.value }))}
                      placeholder="email@example.com"
                      className="bg-[var(--background)]"
                      data-testid="new-lead-email"
                    />
                  </div>
                </div>
                
                <div>
                  <Label>Business Name (optional)</Label>
                  <Input
                    value={newLead.business_name}
                    onChange={(e) => setNewLead(prev => ({ ...prev, business_name: e.target.value }))}
                    placeholder="Company name"
                    className="bg-[var(--background)]"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Lead Source</Label>
                    <Select value={newLead.source} onValueChange={(v) => setNewLead(prev => ({ ...prev, source: v }))}>
                      <SelectTrigger className="bg-[var(--background)]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(sourceLabels).map(([value, label]) => (
                          <SelectItem key={value} value={value}>{label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Preferred Contact</Label>
                    <Select value={newLead.preferred_contact} onValueChange={(v) => setNewLead(prev => ({ ...prev, preferred_contact: v }))}>
                      <SelectTrigger className="bg-[var(--background)]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="phone">Phone</SelectItem>
                        <SelectItem value="email">Email</SelectItem>
                        <SelectItem value="text">Text/SMS</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div>
                  <Label>Service Location</Label>
                  <Input
                    value={newLead.service_location}
                    onChange={(e) => setNewLead(prev => ({ ...prev, service_location: e.target.value }))}
                    placeholder="City or ZIP code"
                    className="bg-[var(--background)]"
                  />
                </div>
                
                <div>
                  <Label>Notes</Label>
                  <Textarea
                    value={newLead.notes}
                    onChange={(e) => setNewLead(prev => ({ ...prev, notes: e.target.value }))}
                    placeholder="Initial notes about this lead..."
                    className="bg-[var(--background)]"
                    rows={3}
                  />
                </div>
                
                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setShowAddModal(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" className="btn-primary" data-testid="save-new-lead-btn">
                    <Plus className="w-4 h-4 mr-2" /> Create Lead
                  </Button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Lead Detail Modal */}
      <AnimatePresence>
        {showDetailModal && selectedLead && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={() => setShowDetailModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-[var(--card)] rounded-2xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-start justify-between mb-6">
                <div>
                  <h3 className="text-xl font-bold text-[var(--text-primary)]">{selectedLead.name}</h3>
                  {selectedLead.business_name && (
                    <p className="text-sm text-[var(--text-muted)] flex items-center gap-1">
                      <Building className="w-4 h-4" /> {selectedLead.business_name}
                    </p>
                  )}
                </div>
                <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium ${statusColors[selectedLead.status]?.bg} ${statusColors[selectedLead.status]?.text} ${statusColors[selectedLead.status]?.border} border`}>
                  {statusLabels[selectedLead.status]}
                </span>
              </div>
              
              {/* Contact Info */}
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="p-4 rounded-xl bg-[var(--background)]">
                  <p className="text-xs text-[var(--text-muted)] mb-1">Phone</p>
                  <p className="text-[var(--text-primary)] flex items-center gap-2">
                    <Phone className="w-4 h-4 text-[var(--primary)]" />
                    {selectedLead.phone || 'Not provided'}
                  </p>
                </div>
                <div className="p-4 rounded-xl bg-[var(--background)]">
                  <p className="text-xs text-[var(--text-muted)] mb-1">Email</p>
                  <p className="text-[var(--text-primary)] flex items-center gap-2">
                    <Mail className="w-4 h-4 text-[var(--primary)]" />
                    {selectedLead.email || 'Not provided'}
                  </p>
                </div>
                <div className="p-4 rounded-xl bg-[var(--background)]">
                  <p className="text-xs text-[var(--text-muted)] mb-1">Location</p>
                  <p className="text-[var(--text-primary)] flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-[var(--primary)]" />
                    {selectedLead.service_location || 'Not specified'}
                  </p>
                </div>
                <div className="p-4 rounded-xl bg-[var(--background)]">
                  <p className="text-xs text-[var(--text-muted)] mb-1">Source</p>
                  <p className="text-[var(--text-primary)]">
                    {sourceLabels[selectedLead.source] || selectedLead.source}
                  </p>
                </div>
              </div>
              
              {/* Notes */}
              {selectedLead.notes && (
                <div className="mb-6">
                  <p className="text-xs text-[var(--text-muted)] mb-2">Notes</p>
                  <p className="text-sm text-[var(--text-secondary)] p-3 rounded-lg bg-[var(--background)]">
                    {selectedLead.notes}
                  </p>
                </div>
              )}
              
              {/* Contact History */}
              <div className="mb-6">
                <h4 className="text-sm font-medium text-[var(--text-primary)] mb-3">Contact History</h4>
                {selectedLead.contact_history?.length > 0 ? (
                  <div className="space-y-3 max-h-48 overflow-y-auto">
                    {selectedLead.contact_history.map((entry, idx) => (
                      <div key={entry.id || idx} className="p-3 rounded-lg bg-[var(--background)] border border-[var(--border)]">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs font-medium text-[var(--text-primary)]">
                            {entry.contact_method} - {entry.outcome}
                          </span>
                          <span className="text-xs text-[var(--text-muted)]">
                            {formatDate(entry.timestamp)}
                          </span>
                        </div>
                        {entry.notes && (
                          <p className="text-xs text-[var(--text-secondary)]">{entry.notes}</p>
                        )}
                        {entry.quote_provided && (
                          <p className="text-xs text-green-400 mt-1">Quote: ${entry.quote_provided}</p>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-[var(--text-muted)] text-center py-4">No contact history yet</p>
                )}
              </div>
              
              {/* Quick Status Update */}
              <div className="mb-6">
                <p className="text-xs text-[var(--text-muted)] mb-2">Quick Status Update</p>
                <div className="flex flex-wrap gap-2">
                  {['contacted', 'interested', 'quote_sent', 'converted', 'not_interested'].map(status => (
                    <Button
                      key={status}
                      size="sm"
                      variant="outline"
                      onClick={() => handleUpdateStatus(selectedLead.id, status)}
                      className={`text-xs ${selectedLead.status === status ? statusColors[status]?.bg : ''}`}
                    >
                      {statusLabels[status]}
                    </Button>
                  ))}
                </div>
              </div>
              
              {/* Actions */}
              <div className="flex justify-between pt-4 border-t border-[var(--border)]">
                <Button variant="outline" onClick={() => setShowDetailModal(false)}>
                  Close
                </Button>
                <Button 
                  className="btn-primary"
                  onClick={() => { setShowDetailModal(false); setShowContactModal(true); }}
                >
                  <PhoneCall className="w-4 h-4 mr-2" /> Log Contact
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Log Contact Modal */}
      <AnimatePresence>
        {showContactModal && selectedLead && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={() => setShowContactModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-[var(--card)] rounded-2xl p-6 w-full max-w-md"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-lg font-bold text-[var(--text-primary)] mb-4 flex items-center gap-2">
                <PhoneCall className="w-5 h-5 text-[var(--primary)]" />
                Log Contact - {selectedLead.name}
              </h3>
              
              <form onSubmit={handleLogContact} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Contact Method</Label>
                    <Select value={contactLog.contact_method} onValueChange={(v) => setContactLog(prev => ({ ...prev, contact_method: v }))}>
                      <SelectTrigger className="bg-[var(--background)]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="phone">Phone Call</SelectItem>
                        <SelectItem value="email">Email</SelectItem>
                        <SelectItem value="sms">Text/SMS</SelectItem>
                        <SelectItem value="in_person">In Person</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Outcome</Label>
                    <Select value={contactLog.outcome} onValueChange={(v) => setContactLog(prev => ({ ...prev, outcome: v }))}>
                      <SelectTrigger className="bg-[var(--background)]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="connected">Connected</SelectItem>
                        <SelectItem value="no_answer">No Answer</SelectItem>
                        <SelectItem value="voicemail">Left Voicemail</SelectItem>
                        <SelectItem value="email_sent">Email Sent</SelectItem>
                        <SelectItem value="meeting_scheduled">Meeting Scheduled</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div>
                  <Label>Quote Provided (optional)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={contactLog.quote_provided}
                    onChange={(e) => setContactLog(prev => ({ ...prev, quote_provided: e.target.value }))}
                    placeholder="$0.00"
                    className="bg-[var(--background)]"
                  />
                </div>
                
                <div>
                  <Label>Next Follow-up Date</Label>
                  <Input
                    type="datetime-local"
                    value={contactLog.next_followup_date}
                    onChange={(e) => setContactLog(prev => ({ ...prev, next_followup_date: e.target.value }))}
                    className="bg-[var(--background)]"
                  />
                </div>
                
                <div>
                  <Label>Notes</Label>
                  <Textarea
                    value={contactLog.notes}
                    onChange={(e) => setContactLog(prev => ({ ...prev, notes: e.target.value }))}
                    placeholder="What was discussed..."
                    className="bg-[var(--background)]"
                    rows={3}
                  />
                </div>
                
                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setShowContactModal(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" className="btn-primary" data-testid="save-contact-log-btn">
                    <CheckCircle className="w-4 h-4 mr-2" /> Log Contact
                  </Button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default LeadsCRMTab;
